"use client"

import {
  DiscordIcon,
  TelegramIcon,
  TwitchIcon,
  XTwitterIcon,
  YouTubeIcon,
} from "@/components/ui/icons"
import { cn } from "@/lib/utils"
import type { CreatorPick, CreatorSocial } from "@/lib/creator-copy/mock"

/* =============================================================================
   Creator home — shared metrics.

   The console speaks the same visual language as the rail's profile panel: the
   stamped TAG chip, the platform glyph map, the label-over-figure stat cell.
   They are re-declared here rather than exported from rail.tsx because the
   rail is a shipped surface and the home console must not reach into it — a
   later rail edit must not be able to restyle this page by accident.
   ============================================================================= */

/** The stamped spec tag, same metrics as the rail's profile chips. */
export const TAG =
  "rounded-[6px] border border-white-200 bg-white-50 px-2 py-1 text-[11px] font-semibold tracking-tight"

/** The product's own glyphs, one per connected account. */
export const SOCIAL_ICON: Record<CreatorSocial["network"], typeof XTwitterIcon> = {
  x: XTwitterIcon,
  youtube: YouTubeIcon,
  telegram: TelegramIcon,
  twitch: TwitchIcon,
  discord: DiscordIcon,
}

export const SOCIAL_LABEL: Record<CreatorSocial["network"], string> = {
  x: "X",
  youtube: "YouTube",
  telegram: "Telegram",
  twitch: "Twitch",
  discord: "Discord",
}

export const SIDE_LABEL: Record<CreatorPick["side"], string> = {
  more: "Over",
  less: "Under",
}

/** One pick as one line: player, side, line, full stat name. Same order as the
    labels the mock ships pre-built, so composed and stored picks read alike. */
export function pickLabel(pick: CreatorPick): string {
  return `${pick.player} ${SIDE_LABEL[pick.side]} ${pick.line} ${pick.statLabel}`
}

/**
 * One section of the console. The heading carries a glyph naming the section's
 * unit and runs a hairline to the page edge — the rule is the section break,
 * so the eye can fall down the page from rule to rule.
 */
export function HomeSection({
  title,
  icon: Icon,
  aside,
  note,
  children,
  className,
}: {
  title: string
  /** The section's glyph. On the label's ramp: a legend, not a decoration. */
  icon?: React.ComponentType<{ className?: string }>
  /** Right end of the heading rule, e.g. a count or a window label. */
  aside?: React.ReactNode
  /** One sentence of context under the heading, caption voice. */
  note?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={className}>
      <div className="flex items-center gap-3">
        <h2 className="flex shrink-0 items-center gap-1.5 font-open-sauce text-[11px] font-semibold tracking-tight text-grey-300">
          {Icon && <Icon aria-hidden className="size-3.5" />}
          {title}
        </h2>
        <span aria-hidden className="h-px flex-1 bg-grey-800" />
        {aside && (
          <span className="shrink-0 text-[11px] font-semibold tracking-tight tabular-nums text-grey-400">
            {aside}
          </span>
        )}
      </div>
      {note && (
        <p className="mt-1.5 text-[11px] leading-relaxed text-grey-400">{note}</p>
      )}
      <div className="mt-3">{children}</div>
    </section>
  )
}

/**
 * Label over figure, the profile panel's record-grid language. Mint and pink
 * keep their codebase meaning here: mint is the creator's own standing, pink
 * is the copy flow, everything else stays white.
 */
export function StatCell({
  label,
  icon: Icon,
  value,
  tone = "white",
}: {
  label: string
  /** The cell's glyph, on the label's ramp. */
  icon?: React.ComponentType<{ className?: string }>
  value: string
  tone?: "white" | "mint" | "pink"
}) {
  return (
    <div className="min-w-0">
      <span className="flex items-center gap-1 text-[11px] font-semibold tracking-tight text-grey-400">
        {Icon && <Icon aria-hidden className="size-3" />}
        {label}
      </span>
      <span
        className={cn(
          "mt-1.5 block font-open-sauce text-[24px] font-black leading-none tracking-[-0.02em] tabular-nums",
          tone === "mint" ? "text-creator" : tone === "pink" ? "text-primary" : "text-white"
        )}
      >
        {value}
      </span>
    </div>
  )
}
